/**
 * Pfad aus Gliedern („Projekt › Antrag › Nachweis“), in dem einzelne Glieder auswählbar sind:
 * Ein Klick öffnet die Auswahl dieser Ebene, bei längeren Listen mit Suche. Feste Glieder sind
 * nur Text.
 *
 * Fachlich weiß die Komponente nichts – welche Glieder es gibt, wie sie heißen und was
 * auswählbar ist, bestimmt der Aufrufer.
 */
import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowDropDown as ArrowDropDownIcon } from "@mui/icons-material";
import { colors } from "../../../styles/theme";

export interface BreadcrumbOption {
  value: string;
  label: string;
}

export interface BreadcrumbSegment {
  key: string;
  label: string;
  /** Noch nichts gewählt („Projekt wählen“) – kursiv */
  placeholder?: boolean;
  /** Auswahl dieser Ebene; ohne sie ist das Glied fest */
  selection?: {
    options: BreadcrumbOption[];
    value: string;
    onChange: (value: string) => void;
  };
}

interface SelectableBreadcrumbProps {
  segments: BreadcrumbSegment[];
  /** Alle Glieder fest, auch die mit Auswahl – etwa in einer Leseansicht */
  readOnly?: boolean;
  /** Meldung unter dem Pfad, etwa ein fehlendes Pflichtglied */
  error?: string;
  ariaLabel?: string;
}

/** Ab so vielen Einträgen hat die Auswahl ein Suchfeld */
const SEARCH_FROM = 8;

const Separator = () => (
  <Typography
    component="span"
    variant="body2"
    sx={{ color: colors.text.secondary, mx: 0.75 }}
  >
    ›
  </Typography>
);

const Segment: React.FC<{ segment: BreadcrumbSegment; readOnly: boolean }> = ({
  segment,
  readOnly,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const selection = readOnly ? undefined : segment.selection;
  const text = (
    <Typography
      component="span"
      variant="body2"
      sx={{
        color: selection ? colors.brand.primary : colors.text.secondary,
        fontStyle: segment.placeholder ? "italic" : "normal",
        wordBreak: "break-word",
      }}
    >
      {segment.label}
    </Typography>
  );
  if (!selection) return text;

  const close = () => {
    setAnchor(null);
    setSearch("");
  };
  const needle = search.trim().toLowerCase();
  const visible = needle
    ? selection.options.filter((o) => o.label.toLowerCase().includes(needle))
    : selection.options;

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        sx={{
          borderRadius: 1,
          px: 0.5,
          mx: -0.5,
          display: "inline-flex",
          alignItems: "center",
          "&:hover": { backgroundColor: colors.state.hover },
        }}
      >
        {text}
        <ArrowDropDownIcon
          sx={{ fontSize: "1.2rem", color: colors.brand.primary }}
        />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        // Das Suchfeld bekommt den Fokus, nicht der erste Eintrag.
        autoFocus={selection.options.length < SEARCH_FROM}
        slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 240 } } }}
      >
        {selection.options.length >= SEARCH_FROM && (
          <Box
            sx={{ px: 1.5, pb: 1 }}
            // Tippen im Suchfeld soll nicht die Tastennavigation des Menüs auslösen.
            onKeyDown={(event) => event.stopPropagation()}
          >
            <TextField
              autoFocus
              size="small"
              fullWidth
              placeholder="Suchen"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Box>
        )}
        {visible.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === selection.value}
            onClick={() => {
              selection.onChange(option.value);
              close();
            }}
          >
            {option.label}
          </MenuItem>
        ))}
        {visible.length === 0 && <MenuItem disabled>Keine Treffer</MenuItem>}
      </Menu>
    </>
  );
};

const SelectableBreadcrumb: React.FC<SelectableBreadcrumbProps> = ({
  segments,
  readOnly = false,
  error,
  ariaLabel = "Pfad",
}) => (
  <Box>
    <Box
      component="nav"
      aria-label={ariaLabel}
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}
    >
      {segments.map((segment, index) => (
        <React.Fragment key={segment.key}>
          {index > 0 && <Separator />}
          <Segment segment={segment} readOnly={readOnly} />
        </React.Fragment>
      ))}
    </Box>
    {error && (
      <Typography
        variant="caption"
        sx={{ color: colors.status.error.main, display: "block", mt: 0.5 }}
      >
        {error}
      </Typography>
    )}
  </Box>
);

export default SelectableBreadcrumb;
